import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useIsAdmin } from './useAdmin';
import { useBrand } from './useBrand';
import { isPlatformModule, FRANKLY_BRAND_SLUG } from '@/lib/platform-modules';

export type ModuleName = 'accounts' | 'criteria' | 'listings' | 'matches' | 'admin' | 'settings' | 'campaigns' | 'inquiries' | 'seller_opps' | 'leads' | 'inbox' | 'unified_inbox' | 'calendar' | 'social_posts' | 'contacts' | 'phone' | 'contracts' | 'outreach' | 'studio';
export type PermissionAction = 'create' | 'read' | 'update' | 'delete';

export interface ActionRegistryEntry {
  module: ModuleName;
  action_key: string;
  label: string;
  description: string | null;
  category: string | null;
  default_granted: boolean;
  sort_order: number;
}

export interface ActionPermission {
  id: string;
  user_id: string;
  module: ModuleName;
  action_key: string;
  granted: boolean;
  created_at: string;
  updated_at: string;
}

export interface ModulePermission {
  id: string;
  user_id: string;
  module: ModuleName;
  can_create: boolean;
  can_read: boolean;
  can_update: boolean;
  can_delete: boolean;
  restrict_to_assigned: boolean;
  created_at: string;
  updated_at: string;
}

export interface BrandModulePermission extends ModulePermission {
  brand_id: string;
}

export interface UserPermissions {
  userId: string;
  permissions: Record<ModuleName, {
    create: boolean;
    read: boolean;
    update: boolean;
    delete: boolean;
    restrictToAssigned: boolean;
  }>;
}

export const ALL_MODULES: ModuleName[] = ['accounts', 'criteria', 'listings', 'matches', 'admin', 'settings', 'campaigns', 'inquiries', 'seller_opps', 'leads', 'inbox', 'unified_inbox', 'calendar', 'social_posts', 'contacts', 'phone', 'contracts', 'outreach', 'studio'];

const DEFAULT_PERM = { create: false, read: false, update: false, delete: false, restrictToAssigned: true };

function buildPermissionsMap(data: ModulePermission[]): UserPermissions['permissions'] {
  const permissions = ALL_MODULES.reduce((acc, module) => {
    acc[module] = { ...DEFAULT_PERM };
    return acc;
  }, {} as UserPermissions['permissions']);

  data?.forEach((perm: ModulePermission) => {
    if (permissions[perm.module]) {
      permissions[perm.module] = {
        create: perm.can_create,
        read: perm.can_read,
        update: perm.can_update,
        delete: perm.can_delete,
        restrictToAssigned: perm.restrict_to_assigned,
      };
    }
  });

  return permissions;
}

// ─── Current user's permissions (brand-scoped) ───

/** Fetch current user's permissions for the active brand */
export function useMyPermissions() {
  const { user } = useAuth();
  const { activeBrandId } = useBrand();

  return useQuery({
    queryKey: ['my_permissions', user?.id, activeBrandId],
    queryFn: async () => {
      if (!user?.id) return null;

      // If no active brand, try global permissions as fallback
      if (!activeBrandId) {
        const { data, error } = await supabase
          .from('user_module_permissions')
          .select('module, can_create, can_read, can_update, can_delete, restrict_to_assigned')
          .eq('user_id', user.id);
        if (error) throw error;
        return {
          userId: user.id,
          permissions: buildPermissionsMap((data ?? []) as ModulePermission[]),
        } as UserPermissions;
      }

      // Brand-scoped permissions
      const { data, error } = await supabase
        .from('user_brand_permissions')
        .select('module, can_create, can_read, can_update, can_delete, restrict_to_assigned')
        .eq('user_id', user.id)
        .eq('brand_id', activeBrandId);

      if (error) throw error;
      return {
        userId: user.id,
        permissions: buildPermissionsMap((data ?? []) as ModulePermission[]),
      } as UserPermissions;
    },
    enabled: !!user?.id,
  });
}

// Check if current user has a specific permission
export function useHasPermission(module: ModuleName, action: PermissionAction) {
  const { data: permissions, isLoading: permissionsLoading } = useMyPermissions();
  const { data: isAdmin, isLoading: adminLoading } = useIsAdmin();

  const isLoading = permissionsLoading || adminLoading;

  if (isAdmin === true) return { hasPermission: true, isLoading: false };
  if (isLoading || !permissions) return { hasPermission: false, isLoading };

  return {
    hasPermission: permissions.permissions[module]?.[action] ?? false,
    isLoading: false,
  };
}

/** Fetch current user's action permissions (brand-scoped) */
export function useMyActionPermissions() {
  const { user } = useAuth();
  const { activeBrandId } = useBrand();

  return useQuery({
    queryKey: ['my_action_permissions', user?.id, activeBrandId],
    queryFn: async () => {
      if (!user?.id) return new Map<string, boolean>();

      // If no active brand, try global fallback
      if (!activeBrandId) {
        const { data, error } = await supabase
          .from('user_action_permissions')
          .select('module, action_key, granted')
          .eq('user_id', user.id);
        if (error) throw error;
        const map = new Map<string, boolean>();
        data?.forEach((row: { module: string; action_key: string; granted: boolean }) => {
          map.set(`${row.module}:${row.action_key}`, row.granted);
        });
        return map;
      }

      const { data, error } = await supabase
        .from('user_brand_action_permissions')
        .select('module, action_key, granted')
        .eq('user_id', user.id)
        .eq('brand_id', activeBrandId);

      if (error) throw error;
      const map = new Map<string, boolean>();
      data?.forEach((row: { module: string; action_key: string; granted: boolean }) => {
        map.set(`${row.module}:${row.action_key}`, row.granted);
      });
      return map;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });
}

/** Hook for checking brand module access */
function useActiveBrandModuleAccess() {
  const { activeBrandId } = useBrand();

  return useQuery({
    queryKey: ['brand-module-access-map', activeBrandId],
    queryFn: async () => {
      if (!activeBrandId) return null;
      const { data, error } = await supabase
        .from('brand_module_access')
        .select('module, enabled')
        .eq('brand_id', activeBrandId);
      if (error) throw error;
      const map = new Map<string, boolean>();
      data?.forEach((row: { module: string; enabled: boolean }) => {
        map.set(row.module, row.enabled);
      });
      return map;
    },
    enabled: !!activeBrandId,
    staleTime: 5 * 60_000,
  });
}

/**
 * Resolve the Frankly Advisors brand ID from the user's brand list.
 * Returns null if the user does not have access to Frankly Advisors.
 */
function useFranklyBrandId(): string | null {
  const { brands } = useBrand();
  return brands.find(b => b.slug === FRANKLY_BRAND_SLUG)?.id ?? null;
}

/**
 * Fetch current user's permissions scoped to the Frankly Advisors brand.
 * Used for platform modules (leads, campaigns, inbox) which are always
 * evaluated against the Frankly brand regardless of which brand is active.
 */
function useFranklyBrandPermissions() {
  const { user } = useAuth();
  const franklyBrandId = useFranklyBrandId();

  return useQuery({
    queryKey: ['my_permissions_frankly', user?.id, franklyBrandId],
    queryFn: async () => {
      if (!user?.id || !franklyBrandId) return null;

      const { data, error } = await supabase
        .from('user_brand_permissions')
        .select('module, can_create, can_read, can_update, can_delete, restrict_to_assigned')
        .eq('user_id', user.id)
        .eq('brand_id', franklyBrandId);

      if (error) throw error;
      return {
        userId: user.id,
        permissions: buildPermissionsMap((data ?? []) as ModulePermission[]),
      } as UserPermissions;
    },
    enabled: !!user?.id && !!franklyBrandId,
    staleTime: 5 * 60_000,
  });
}

/**
 * Fetch current user's action permissions scoped to the Frankly Advisors brand.
 * Used for platform modules (leads, campaigns, inbox) which always evaluate
 * action permissions against the Frankly brand regardless of which brand is active.
 */
function useFranklyBrandActionPermissions() {
  const { user } = useAuth();
  const franklyBrandId = useFranklyBrandId();

  return useQuery({
    queryKey: ['my_action_permissions_frankly', user?.id, franklyBrandId],
    queryFn: async () => {
      if (!user?.id || !franklyBrandId) return new Map<string, boolean>();

      const { data, error } = await supabase
        .from('user_brand_action_permissions')
        .select('module, action_key, granted')
        .eq('user_id', user.id)
        .eq('brand_id', franklyBrandId);

      if (error) throw error;
      const map = new Map<string, boolean>();
      data?.forEach((row: { module: string; action_key: string; granted: boolean }) => {
        map.set(`${row.module}:${row.action_key}`, row.granted);
      });
      return map;
    },
    enabled: !!user?.id && !!franklyBrandId,
    staleTime: 5 * 60 * 1000,
  });
}

// Hook for checking multiple permissions at once (CRUD + action-level, brand-aware)
export function usePermissions() {
  const { data: permissions, isLoading: permissionsLoading } = useMyPermissions();
  const { data: actionPermissions, isLoading: actionLoading } = useMyActionPermissions();
  const { data: franklyActionPermissions } = useFranklyBrandActionPermissions();
  const { data: isAdmin, isLoading: adminLoading } = useIsAdmin();
  const { data: brandModuleAccess } = useActiveBrandModuleAccess();
  const franklyBrandId = useFranklyBrandId();
  const { data: franklyPermissions } = useFranklyBrandPermissions();

  const isLoading = permissionsLoading || adminLoading || actionLoading;

  const can = (module: ModuleName, action: PermissionAction): boolean => {
    if (isAdmin === true) return true;

    // Platform modules (leads, campaigns, inbox): require Frankly Advisors
    // brand access and check permissions from the Frankly brand, not the
    // active brand.  Skip the per-brand module access toggle entirely.
    if (isPlatformModule(module)) {
      if (!franklyBrandId) return false;
      if (!franklyPermissions) return false;
      return franklyPermissions.permissions[module]?.[action] ?? false;
    }

    // Deal / other modules: brand-scoped as before
    if (brandModuleAccess && !brandModuleAccess.get(module)) return false;
    if (!permissions) return false;
    return permissions.permissions[module]?.[action] ?? false;
  };

  const canAction = (module: ModuleName, actionKey: string): boolean => {
    if (isAdmin === true) return true;

    // Platform modules: use Frankly brand action permissions
    if (isPlatformModule(module)) {
      if (!franklyBrandId) return false;
      if (!franklyActionPermissions) return false;
      return franklyActionPermissions.get(`${module}:${actionKey}`) ?? false;
    }

    // Deal / other modules: brand-scoped as before
    if (brandModuleAccess && !brandModuleAccess.get(module)) return false;
    if (!actionPermissions) return false;
    return actionPermissions.get(`${module}:${actionKey}`) ?? false;
  };

  const restrictToAssigned = (module: ModuleName): boolean => {
    if (isAdmin === true) return false;

    // Platform modules: use Frankly brand permissions
    if (isPlatformModule(module)) {
      return franklyPermissions?.permissions[module]?.restrictToAssigned ?? true;
    }

    if (!permissions) return true;
    return permissions.permissions[module]?.restrictToAssigned ?? true;
  };

  return { can, canAction, restrictToAssigned, isLoading, isAdmin: isAdmin === true };
}

// Helper hook for checking if user should only see assigned records
export function useRestrictToAssigned(module: ModuleName) {
  const { data: permissions, isLoading: permissionsLoading } = useMyPermissions();
  const { data: isAdmin, isLoading: adminLoading } = useIsAdmin();

  const isLoading = permissionsLoading || adminLoading;

  if (isAdmin === true) return { restrictToAssigned: false, isLoading: false };
  if (isLoading || !permissions) return { restrictToAssigned: true, isLoading };

  return {
    restrictToAssigned: permissions.permissions[module]?.restrictToAssigned ?? true,
    isLoading: false,
  };
}

// ─── Admin hooks (for managing other users' permissions) ───

/** Fetch all users' permissions (admin only) — global table */
export function useAllUserPermissions() {
  return useQuery({
    queryKey: ['all_user_permissions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_module_permissions')
        .select('*')
        .order('user_id');
      if (error) throw error;
      return data as ModulePermission[];
    },
  });
}

/** Fetch permissions for a specific user+brand (admin/brand admin) */
export function useUserBrandPermissions(userId: string | undefined, brandId: string | null) {
  return useQuery({
    queryKey: ['user_brand_permissions', userId, brandId],
    queryFn: async () => {
      if (!userId || !brandId) return [];
      const { data, error } = await supabase
        .from('user_brand_permissions')
        .select('*')
        .eq('user_id', userId)
        .eq('brand_id', brandId);
      if (error) throw error;
      return data as BrandModulePermission[];
    },
    enabled: !!userId && !!brandId,
  });
}

/** Legacy: Fetch global permissions for a specific user (admin only) */
export function useUserPermissions(userId: string | undefined) {
  return useQuery({
    queryKey: ['user_permissions', userId],
    queryFn: async () => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from('user_module_permissions')
        .select('*')
        .eq('user_id', userId);
      if (error) throw error;
      return data as ModulePermission[];
    },
    enabled: !!userId,
  });
}

/** Upsert brand-scoped permission for a user */
export function useUpsertBrandPermission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      brandId,
      module,
      canCreate,
      canRead,
      canUpdate,
      canDelete,
      restrictToAssigned,
    }: {
      userId: string;
      brandId: string;
      module: ModuleName;
      canCreate: boolean;
      canRead: boolean;
      canUpdate: boolean;
      canDelete: boolean;
      restrictToAssigned: boolean;
    }) => {
      const { data, error } = await supabase
        .from('user_brand_permissions')
        .upsert({
          user_id: userId,
          brand_id: brandId,
          module,
          can_create: canCreate,
          can_read: canRead,
          can_update: canUpdate,
          can_delete: canDelete,
          restrict_to_assigned: restrictToAssigned,
        }, {
          onConflict: 'user_id,brand_id,module',
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['user_brand_permissions', variables.userId, variables.brandId] });
      queryClient.invalidateQueries({ queryKey: ['my_permissions', variables.userId] });
    },
  });
}

/** Legacy: Upsert global permission for a user */
export function useUpsertPermission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      module,
      canCreate,
      canRead,
      canUpdate,
      canDelete,
      restrictToAssigned,
    }: {
      userId: string;
      module: ModuleName;
      canCreate: boolean;
      canRead: boolean;
      canUpdate: boolean;
      canDelete: boolean;
      restrictToAssigned: boolean;
    }) => {
      const { data, error } = await supabase
        .from('user_module_permissions')
        .upsert({
          user_id: userId,
          module,
          can_create: canCreate,
          can_read: canRead,
          can_update: canUpdate,
          can_delete: canDelete,
          restrict_to_assigned: restrictToAssigned,
        }, {
          onConflict: 'user_id,module',
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['user_permissions', variables.userId] });
      queryClient.invalidateQueries({ queryKey: ['all_user_permissions'] });
      queryClient.invalidateQueries({ queryKey: ['my_permissions', variables.userId] });
    },
  });
}

// Delete a permission (admin only)
export function useDeletePermission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, module }: { userId: string; module: ModuleName }) => {
      const { error } = await supabase
        .from('user_module_permissions')
        .delete()
        .eq('user_id', userId)
        .eq('module', module);
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['user_permissions', variables.userId] });
      queryClient.invalidateQueries({ queryKey: ['all_user_permissions'] });
      queryClient.invalidateQueries({ queryKey: ['my_permissions', variables.userId] });
    },
  });
}

// Fetch the action permission registry (available actions per module)
export function useActionPermissionRegistry() {
  return useQuery({
    queryKey: ['action_permission_registry'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('action_permission_registry')
        .select('*')
        .order('sort_order');
      if (error) throw error;
      const grouped: Record<string, ActionRegistryEntry[]> = {};
      (data as ActionRegistryEntry[]).forEach((entry) => {
        if (!grouped[entry.module]) grouped[entry.module] = [];
        grouped[entry.module].push(entry);
      });
      return grouped;
    },
    staleTime: 30 * 60 * 1000,
  });
}

/** Fetch brand-scoped action permissions for a specific user */
export function useUserBrandActionPermissions(userId: string | undefined, brandId: string | null) {
  return useQuery({
    queryKey: ['user_brand_action_permissions', userId, brandId],
    queryFn: async () => {
      if (!userId || !brandId) return [];
      const { data, error } = await supabase
        .from('user_brand_action_permissions')
        .select('*')
        .eq('user_id', userId)
        .eq('brand_id', brandId);
      if (error) throw error;
      return data as (ActionPermission & { brand_id: string })[];
    },
    enabled: !!userId && !!brandId,
  });
}

/** Legacy: Fetch global action permissions for a specific user (admin only) */
export function useUserActionPermissions(userId: string | undefined) {
  return useQuery({
    queryKey: ['user_action_permissions', userId],
    queryFn: async () => {
      if (!userId) return [];
      const { data, error } = await supabase
        .from('user_action_permissions')
        .select('*')
        .eq('user_id', userId);
      if (error) throw error;
      return data as ActionPermission[];
    },
    enabled: !!userId,
  });
}

/** Bulk upsert brand-scoped action permissions */
export function useBulkUpsertBrandActionPermissions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      brandId,
      actions,
    }: {
      userId: string;
      brandId: string;
      actions: { module: ModuleName; action_key: string; granted: boolean }[];
    }) => {
      if (actions.length === 0) return;
      const rows = actions.map((a) => ({
        user_id: userId,
        brand_id: brandId,
        module: a.module,
        action_key: a.action_key,
        granted: a.granted,
      }));
      const { error } = await supabase
        .from('user_brand_action_permissions')
        .upsert(rows, { onConflict: 'user_id,brand_id,module,action_key' });
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['user_brand_action_permissions', variables.userId, variables.brandId] });
      queryClient.invalidateQueries({ queryKey: ['my_action_permissions', variables.userId] });
    },
  });
}

/** Legacy: Bulk upsert global action permissions for a user (admin only) */
export function useBulkUpsertActionPermissions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      actions,
    }: {
      userId: string;
      actions: { module: ModuleName; action_key: string; granted: boolean }[];
    }) => {
      if (actions.length === 0) return;
      const rows = actions.map((a) => ({
        user_id: userId,
        module: a.module,
        action_key: a.action_key,
        granted: a.granted,
      }));
      const { error } = await supabase
        .from('user_action_permissions')
        .upsert(rows, { onConflict: 'user_id,module,action_key' });
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['user_action_permissions', variables.userId] });
      queryClient.invalidateQueries({ queryKey: ['my_action_permissions', variables.userId] });
    },
  });
}
